import type { Sample } from '@ctcells/contract'
import { maskPii, t } from '@ctcells/i18n'

import { sampleTestInfoRows } from '../samples/sampleIdentityRows.ts'

/**
 * 작업 추가 창의 웰별 시료 고르기 — 배정 표(트레이 오른쪽)와 웰 선택 창이 **같이 쓰는 시료 열**(eric 2026-10-27 「주요한 거 다 보이게 해줘」).
 * 값은 시료 목록 응답(`Sample`)에 실린 것만 — 시료 번호 · 환자 번호 · 타입 · 암종 · 채혈 일시. 라벨은 시료 상세 · 서랍과 같은 사전 키다
 * (`sampleIdentityRows.ts`). 🔴 시료 번호 · 환자 번호는 PII 가림(`maskPii`) 그대로. 빈 값은 `undefined` — 부르는 쪽이 대시로 그린다.
 * 🔴 응답에 없는 값(현재 위치 · 공정 단계 등)은 지어내지 않는다.
 */
export interface WellSampleColumn {
  key: 'sampleId' | 'patient' | 'sampleType' | 'cancerType' | 'drawDate'
  header: string
  /** 그릴 글자(마스킹 뒤). 비었으면 `undefined` */
  value: (sample: Sample, view: boolean) => string | undefined
  /** 식별자 꼴(고정폭 글꼴) */
  mono: boolean
}

const text = (value: string | null | undefined): string | undefined =>
  value == null || value.trim() === '' ? undefined : value.trim()

/** 검사 정보 줄(시료 상세 · 서랍과 같은 값 · 같은 꼴)에서 그 칸 하나 */
const infoOf = (sample: Sample, key: 'drawDate' | 'cancerType'): string | undefined =>
  text(sampleTestInfoRows(sample).find((row) => row.key === key)?.value)

export function wellSampleColumns(): readonly WellSampleColumn[] {
  return [
    {
      key: 'sampleId',
      header: t('column.sampleId'),
      mono: true,
      value: (s, view) =>
        text(s.sample_id) === undefined ? undefined : maskPii(s.sample_id, view),
    },
    {
      key: 'patient',
      header: t('column.patientId'),
      mono: true,
      value: (s, view) =>
        text(s.patient_id) === undefined ? undefined : maskPii(s.patient_id, view),
    },
    {
      key: 'sampleType',
      header: t('field.sampleType'),
      mono: false,
      value: (s) => text(s.sample_type),
    },
    {
      key: 'cancerType',
      header: t('field.cancerType'),
      mono: false,
      value: (s) => infoOf(s, 'cancerType'),
    },
    {
      key: 'drawDate',
      header: t('field.drawAt'),
      mono: true,
      value: (s) => infoOf(s, 'drawDate'),
    },
  ]
}
